import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon, useErrorDialog } from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';

/**
 * Files on a ticket, from the desk's side.
 *
 * ── THE SAME THREE STEPS AS THE PLAYER'S, AND A DIFFERENT WARNING ──────────
 *
 * Ask, PUT to the store, confirm. What differs is who is reading: an operator
 * is about to open a file a member of the public uploaded, and that file has
 * NOT been scanned — nothing in this platform can scan it, because no scanning
 * service has been procured. So the panel says so.
 *
 * That sentence is not defensive boilerplate. Without it the desk reasonably
 * assumes the platform checked, because platforms usually do; with it, the
 * operator knows the only guarantees are the type and the size. The file is
 * also always served as a download rather than rendered — see the driver's
 * `presignDownload`, which signs `Content-Disposition: attachment` so it
 * cannot be dropped — because a file that renders inline renders on this
 * origin, inside this session.
 */

interface Attachment {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedByKind: 'PLAYER' | 'STAFF';
  createdAt: string;
}

const ACCEPT = 'image/png,image/jpeg,image/webp,image/heic,application/pdf';
const MAX_BYTES = 10 * 1024 * 1024;

function readableSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function TicketAttachments({
  ticketId,
  canRespond,
}: {
  ticketId: string;
  /** Reading takes `players.view`; attaching takes `support.respond`. */
  canRespond: boolean;
}) {
  const { showError } = useErrorDialog();
  const [items, setItems] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ items: Attachment[] }>(
        `/api/support/tickets/${ticketId}/attachments`,
      );
      setItems(data.items);
      setFailed(false);
    } catch {
      /* NOT an empty list. "No files" and "we could not find out" are different
         facts, and showing the first when the second is true tells an operator
         a player sent nothing when they may have sent everything. */
      setFailed(true);
    }
  }, [ticketId]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async (file: File): Promise<void> => {
    if (file.size > MAX_BYTES) {
      showError({
        title: 'That file is too large',
        message: `${file.name} is ${readableSize(file.size)}. The limit is 10 MB.`,
      });
      return;
    }

    setBusy(true);
    try {
      const reserved = await api.post<{
        attachmentId: string;
        url: string;
        headers: Record<string, string>;
      }>(`/api/support/tickets/${ticketId}/attachments`, {
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });

      /* STRAIGHT TO THE STORE, with the headers replayed exactly: a presigned
         URL signs the request it expects, so a PUT carrying a different
         Content-Type is refused with a signature error that mentions nothing
         about content types. */
      const put = await fetch(reserved.url, {
        method: 'PUT',
        headers: reserved.headers,
        body: file,
      });
      if (!put.ok) {
        showError({
          title: 'That file did not reach the store',
          message:
            'Nothing has been attached to this ticket. The upload goes from this browser straight ' +
            'to storage, so this is usually a network problem at this end rather than a platform ' +
            'fault. Try again.',
        });
        return;
      }

      await api.post<{ attachment: Attachment }>(
        `/api/support/tickets/${ticketId}/attachments/${reserved.attachmentId}/confirm`,
      );
      await load();
    } catch (error) {
      showError({
        title: 'Could not attach that file',
        message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
      });
    } finally {
      setBusy(false);
      if (picker.current) picker.current.value = '';
    }
  };

  const open = async (attachment: Attachment): Promise<void> => {
    try {
      const link = await api.get<{ url: string }>(
        `/api/support/tickets/${ticketId}/attachments/${attachment.id}/link`,
      );
      /*
       * A NEW TAB, VIA AN ANCHOR — AND WHAT IS ACTUALLY KNOWN ABOUT WHY.
       *
       * The link answers with `content-disposition: attachment`, so a browser that
       * honours it saves the file and the blank tab closes itself. That header is
       * the mechanism, and it is the ONLY one that works for both drivers:
       * `download` is ignored cross-origin, which is every signed S3 URL, so the
       * attribute is deliberately not set. Two mechanisms that can disagree are
       * worse than one that always applies.
       *
       * `_blank` because an operator mid-conversation must not lose the ticket they
       * are reading, and `noreferrer` alongside `noopener` because the target is a
       * file somebody outside this company uploaded and has no business learning
       * which screen sent them to it.
       *
       * An anchor rather than `window.open` so no popup blocker is involved.
       *
       * WHAT WAS CLAIMED HERE BEFORE AND IS FALSE: that Chrome silently drops a
       * download started from a `noopener` popup. A clean Chromium was measured
       * doing it three ways — anchor with `download`, anchor without, and
       * `window.open` — and downloaded the file every time. The report that started
       * this (a click that failed with "Site wasn't available" while the identical
       * URL pasted into the address bar saved at once) is REAL and UNEXPLAINED. It
       * reproduces on one Chrome profile, in normal and incognito windows, and on
       * no other browser tried. The platform side was measured correct every way
       * available: 175,103 bytes of valid PNG, the right headers, through both the
       * API port and the dev-server proxy. Nothing above is a fix for it; the shape
       * of the code is what it is for the reasons given, and the open question is
       * recorded rather than papered over with a plausible cause.
       */
      const a = document.createElement('a');
      a.href = link.url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      /*
       * REMOVED LATER, NOT IN THE SAME TICK — a precaution, not a measured
       * fix. Removing the element in the same tick as the click is a known
       * way to cancel a navigation the browser has only just started, and
       * nothing needs the anchor gone that urgently. It was one of the
       * theories tried against the unexplained failure above and it did not
       * resolve it; it stays because a deferred removal costs nothing.
       */
      setTimeout(() => a.remove(), 0);
    } catch (error) {
      showError({
        title: 'Could not open that file',
        message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
      });
    }
  };

  return (
    <section className="stack" style={{ gap: 'var(--space-3)' }}>
      <h3 className="text-subtle" style={{ fontSize: '11.5px', textTransform: 'uppercase' }}>
        Files on this ticket
      </h3>

      {failed ? (
        <p className="text-subtle" style={{ fontSize: '12px' }}>
          The file list could not be read, so none is shown — this is <strong>not</strong> the same
          as there being none.{' '}
          <button type="button" className="link-button" onClick={() => void load()}>
            Try again
          </button>
        </p>
      ) : items.length === 0 ? (
        <p className="text-subtle" style={{ fontSize: '12px' }}>
          Nothing attached.
        </p>
      ) : (
        <ul className="stack" style={{ gap: 'var(--space-2)', listStyle: 'none', padding: 0 }}>
          {items.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                className="attachment-row"
                onClick={() => void open(a)}
                title={`Download ${a.filename}`}
              >
                <Icon name={a.contentType === 'application/pdf' ? 'file-pdf' : 'image'} />
                <span className="attachment-name">{a.filename}</span>
                <span className="text-subtle" style={{ fontSize: '11px' }}>
                  {readableSize(a.sizeBytes)} ·{' '}
                  {a.uploadedByKind === 'PLAYER' ? 'from the player' : `from ${a.uploadedBy}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={picker}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void send(file);
        }}
      />

      <Button
        variant="secondary"
        busy={busy}
        disabled={!canRespond}
        /* The honesty rule: a disabled control states its real reason. */
        title={
          canRespond
            ? 'Attach a file to this ticket'
            : 'Attaching a file writes to the thread, which needs the support-respond capability.'
        }
        onClick={() => picker.current?.click()}
      >
        <Icon name="paperclip" />
        Attach a file
      </Button>

      {/*
        THE SHARING RULE IS STATED, because the control below this panel
        promises the opposite. "Internal note — staff only" sits directly
        underneath, and an operator who has just used it can reasonably assume a
        file is as private as a note. It is not: `support.ticket_attachment` has
        no internal column, so there is nowhere to record a private file and
        every file here reaches the player under the name of whoever sent it.
      */}
      <p className="text-subtle" style={{ fontSize: '11px' }}>
        Images or PDF, up to 10 MB. <strong>The player sees every file here</strong>, named as
        sent by you — an internal note is private, a file is not.{' '}
        <strong>Files are not scanned for viruses</strong> — nothing in this platform can scan
        them. They open in a new tab and save to your computer; nothing a player sent renders
        inside the console.
      </p>
    </section>
  );
}
